import { FC, useState, useContext, useEffect } from 'react';
import { Row, Col, Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { AppContext } from 'context/AppContext';
import { useJoinedCampaigns } from 'services/EnrollmentService';
import { useFeed } from 'services/FeedService';
import CampaignItem from './CampaignItem';
import MyCampaignsList from './MyCampaignsList';
import FeedList from '../../Feed/components/FeedList';
import MobileHotCampaigns from './MobileHotCampaigns';

import './HotCampaignList.css'

const { TabPane } = Tabs;

interface Props {
  hotCampaigns: [];
  otherCampaigns: [];
}

const HotCampaignList: FC<Props> = ({ hotCampaigns, otherCampaigns, ...props }) => {
  const { t } = useTranslation();
  const { authentication, commonData, api } = useContext(AppContext);
  const { authKey } = authentication;
  const { userDetails } = commonData;
  const [activeTab, setActiveTab] = useState('');

  // Get user details if not available
  const { data: userDetailsResponse } = useSWR(
    authKey && !userDetails ? 'user-details' : null,
    () => api.profile.getUserDetails(),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const currentUser = userDetails || (userDetailsResponse?.error ? null : (userDetailsResponse?.results?.[0] || userDetailsResponse));

  // Check if user has joined campaigns to determine tab visibility
  const { data: myCampaignsData, isLoading: myCampaignsLoading } = useJoinedCampaigns(
    currentUser?.id || '',
    '-campaign__campaign_order',
    1,
    null, // Show all campaigns for checking
    authKey
  );

  // Check if user has feed items
  const { data: feedData, isLoading: feedLoading } = useFeed(1, authKey);
  const hasFeedItems = !feedLoading && feedData?.results && feedData.results.length > 0;
  const hasMyCampaigns = !myCampaignsLoading && myCampaignsData?.results && myCampaignsData.results.length > 0;

  // Always select the first available tab based on current state
  useEffect(() => {
    // Only run when data is loaded to avoid premature tab setting
    if (!feedLoading && !myCampaignsLoading) {
      let firstAvailableTab = 'hot'; // Default fallback
      
      // Determine first available tab based on login state and data
      if (authKey && hasFeedItems) {
        firstAvailableTab = 'feed';
      } else if (authKey) {
        firstAvailableTab = 'my';
      } else {
        firstAvailableTab = 'hot';
      }
      
      // Always set to first available tab if not already set or if current tab is not available
      if (!activeTab || 
          (activeTab === 'feed' && (!authKey || !hasFeedItems)) ||
          (activeTab === 'my' && !authKey) ||
          (activeTab === 'hot' && authKey && hasFeedItems)) {
        setActiveTab(firstAvailableTab);
      }
    }
  }, [feedLoading, myCampaignsLoading, authKey, hasFeedItems, activeTab]);

  return (
    <div className={`HotCampaignList ${authKey ? 'logged-in' : ''}`}>
      {/* Hot Campaigns Horizontal Scroll - Mobile always, Web when logged in */}
      <MobileHotCampaigns hotCampaigns={hotCampaigns} t={t} />
      
      {/* Mobile More Campaigns Section */}
      {(!authKey || (!myCampaignsLoading && !hasMyCampaigns)) && otherCampaigns.length > 0 && (
        <div className="mobile-more-campaigns">
          <div className="mobile-more-campaigns-title">{t('#Home.others_campaigns')}</div>
          <Row gutter={[16, 16]} className="mobile-more-campaigns-grid">
            {otherCampaigns.map((item, i) => <Col key={i} xs={24}>
              <CampaignItem item={item} />
            </Col>)}
          </Row>
        </div>
      )}

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        className='campaign-tabs'
        key={`tabs-${authKey ? 'logged' : 'guest'}-${activeTab}`}
      >
        {/* Show Feed tab first when user is logged in and has feed items */}
        {authKey && hasFeedItems && (
          <TabPane tab={t('#Feed.feeds')} key="feed">
            <FeedList />
          </TabPane>
        )}

        {/* Hot Campaigns Tab - Only show when user is not logged in */}
        {!authKey && (
          <TabPane tab={t('#Home.hot_campaigns')} key="hot" forceRender>
            <div key={`hot-content-${authKey ? 'logged' : 'guest'}-${hotCampaigns.length}`}>
              <Row gutter={[16, 16]}>
                {hotCampaigns.map((item, i) => <Col key={`hot-${i}`} xs={24} lg={8}>
                  <CampaignItem item={item} />
                </Col>)}
              </Row>
            </div>
          </TabPane>
        )}

        {/* Only show More Campaigns tab when user doesn't have My Campaigns or is not logged in */}
        {(!authKey || (!myCampaignsLoading && !hasMyCampaigns)) && (
          <TabPane tab={t('#Home.others_campaigns')} key="other" forceRender>
            <div key={`other-content-${authKey ? 'logged' : 'guest'}-${otherCampaigns.length}`}>
              <Row gutter={[16, 16]}>
                {otherCampaigns.map((item, i) => <Col key={`other-${i}`} xs={24} lg={8}>
                  <CampaignItem item={item} />
                </Col>)}
              </Row>
            </div>
          </TabPane>
        )}

        {/* Show My Campaigns tab when user is logged in */}
        {authKey && (
          <TabPane tab={t('#Home.my_campaigns')} key="my">
            <MyCampaignsList />
          </TabPane>
        )}
      </Tabs>
    </div>
  );
}

export default HotCampaignList;
